import Alpine from "alpinejs";
import algoliasearch from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import { searchBox, hits, pagination, refinementList, configure } from "instantsearch.js/es/widgets";

Alpine.data("algoliaSearch", (config) => {
    return {
        config: {
            /**
             * Props are coming from light-modules/sanofi-lm-platform/templates/areas/sanofi-platform/search/searchAlgolia.ftl
             */
            algoliaAppId: "",
            algoliaAnonymousKey: "",
            algoliaAuthenticatedKey: "",
            algoliaIndexName: "",
            cssClassForm: "",
            cssClassInput: "",
            cssClassIcon: "",
            cssClassResultTitle: "",
            cssClassResultLink: "",
            cssClassResultDescription: "",
            cssClassPaginationTextColor: "",
            cssClassPaginationHoverTextColor: "",
            noResultsMessage: "",
            showCategories: false,
            refinementListCountBackground: "",
            refinementListCountText: "",
            refinemenListFilterBy: "",
            summaryDisplay: "",
            isAnonymous: "",
            isRolesEnabled: "",
            userPluralValue: "",
            ...config,
        },

        widgetContainerSearchBox: ".as-searchbox",
        widgetContainerHits: ".as-hits",
        widgetContainerPagination: ".as-pagination",

        renderResultsOnLoad: false,
        isShowingResults: false,
        hasResults: false,
        instantsearch: null,

        hasCategories() {
            return !!this.$refs.showCategories;
        },

        isAnonymous() {
            if (this.config.isAnonymous === "true") return true;
        },

        init() {
            const self = this;
            let algoliaClient = algoliasearch(this.config.algoliaAppId, this.config.algoliaAuthenticatedKey);
            // Set no results message from DOM template
            this.config.noResultsMessage = self.$refs.noResults.innerHTML.toString();

            if (this.isAnonymous()) {
                algoliaClient = algoliasearch(this.config.algoliaAppId, this.config.algoliaAnonymousKey);
            }

            const searchClient = {
                ...algoliaClient,
                search(requests) {
                    if (!self.renderResultsOnLoad) {
                        /**
                         * Do not render results on pageload
                         * https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/js/#detecting-empty-search-requests
                         */
                        if (requests.every(({ params }) => !params.query)) {
                            self.isShowingResults = false;
                            return Promise.resolve({
                                results: requests.map(() => ({
                                    hits: [],
                                    nbHits: 0,
                                    nbPages: 0,
                                    page: 0,
                                    processingTimeMS: 0,
                                })),
                            });
                        }
                    }

                    self.isShowingResults = true;
                    return algoliaClient.search(requests);
                },
            };

            const algoliaIndexName = this.config.algoliaIndexName;

            let instantsearchProps = {
                indexName: algoliaIndexName,
                searchClient: searchClient,
            };

            let urlParams = new URLSearchParams(window.location.search);
            let queryString = urlParams.get("q");
            if (queryString) {
                instantsearchProps.routing = {
                    stateMapping: {
                        stateToRoute(uiState) {
                            const indexUiState = uiState[algoliaIndexName] || {};
                            return {
                                q: indexUiState.query,
                            };
                        },
                        routeToState(routeState) {
                            return {
                                [algoliaIndexName]: {
                                    query: routeState.q,
                                },
                            };
                        },
                    },
                };
            }

            this.instantsearch = instantsearch(instantsearchProps);

            let filterRole = `(roles:'${this.config.userPluralValue}' OR roles:'all')`;
            let filterLang = `targetLang:${this.config.language}`;
            let accountRolesEnabled = this.config.isRolesEnabled === "true" && this.config.userPluralValue !== "false";

            let filterTarget = this.config.isAnonymous === "true" && !accountRolesEnabled ? "roles:'all'" : filterRole;
            let filterQuery = `${filterLang} AND ${filterTarget}`;

            const widgetList = [
                configure({
                    attributesToSnippet: ["content:50", "description:50"],
                    filters: filterQuery,
                }),
                /**
                 * Searchbox
                 * https://www.algolia.com/doc/api-reference/widgets/search-box/js/
                 */
                searchBox({
                    container: this.widgetContainerSearchBox,
                    showReset: true,
                    showLoadingIndicator: false,
                    cssClasses: {
                        root: "py-8",
                        form: `flex rounded-full shadow-xl ${self.config?.cssClassForm} w-11/12 md:w-10/12 lg:w-7/12 mx-auto overflow-hidden`,
                        input: `h-12 md:h-20 lg:h-24 text-2xl w-full py-2 md:py-4 leading-tight ${self.config?.cssClassInput} focus:outline-none placeholder-black`,
                        submit: `order-first p-2 focus:outline-none w-24 flex items-center justify-center ${self.config?.cssClassIcon}`,
                        submitIcon: `h-6 w-6`,
                        reset: `order-last p-2 focus:outline-none w-24 flex items-center justify-center ${self.config?.cssClassIcon}`,
                        resetIcon: "h-6 w-6",
                    },
                    queryHook: (query, search) => {
                        // Clear categories refinement on a new search
                        if (this.hasCategories()) {
                            this.instantsearch.helper.clearRefinements("categories");
                        }
                        search(query);
                    },
                }),

                /*
                 * Hits
                 * https://www.algolia.com/doc/api-reference/widgets/hits/js/
                 */
                hits({
                    container: this.widgetContainerHits,
                    templates: {
                        item(hit, { html, components }) {
                            let summary = self.config?.summaryDisplay !== "none" ? html`<p class="text-base ${self.config?.cssClassResultDescription}">${components.Snippet({ attribute: self.config?.summaryDisplay, hit, highlightedTagName: "mark" })}</p>` : "";

                            return html`<a href="${hit.url}" class="block py-6 border-b-2">
                                <h3 class="font-title text-2xl ${self.config?.cssClassResultTitle}">${components.Highlight({ attribute: "title", hit })}</h3>
                                <p class="text-xs ${self.config?.cssClassResultLink}">${hit.url}</p>
                                ${summary}
                            </a>`;
                        },
                        empty(results, { html }) {
                            return html`${self.config.noResultsMessage.replaceAll("${query}", results.query)}`;
                        },
                    },

                    /**
                     * This is helpful to review the schema of results from Algolia
                     */
                    // transformItems(items) {
                    //     console.log(items);
                    //     return items;
                    // },
                }),

                /**
                 * Pagination
                 * https://www.algolia.com/doc/api-reference/widgets/pagination/js/
                 */
                pagination({
                    container: this.widgetContainerPagination,
                    cssClasses: {
                        root: "flex justify-center py-8 font-body",
                        list: "flex",
                        selectedItem: "font-bold",
                        disabledItem: "hidden",
                        link: `p-2 text-xl ${self.config?.cssClassPaginationTextColor} ${self.config?.cssClassPaginationHoverTextColor}`,
                    },
                }),
            ];

            if (this.hasCategories()) {
                /**
                 * Refinement List
                 * https://www.algolia.com/doc/api-reference/widgets/refinement-list/js/
                 */
                widgetList.push(
                    refinementList({
                        container: "#refinementList",
                        attribute: "categories",
                        operator: `${self.config?.refinementListFilterBy}`,
                        cssClasses: {
                            root: "w-full md:w-48 py-5 mr-7 flex-none",
                            item: "leading-3 p-1 flex",
                            label: "flex items-center",
                            checkbox: "p1",
                            labelText: "leading-none md:leading-tight px-1 py-0 capitalize",
                            count: `py-0.5 px-1 ${self.config?.refinementListCountBackground} opacity-50 border border-solid border-inherit rounded-lg ${self.config?.refinementListCountText} text-xs`,
                        },
                    })
                );
            }

            this.instantsearch.addWidgets(widgetList);

            this.instantsearch.start();

            this.instantsearch.on("render", () => {
                const noOfResults = this.instantsearch.helper.lastResults?.nbHits || 0;
                this.hasResults = noOfResults > 0;
            });

        },
    };
});
